import { useEffect, useRef, useState } from "react";
import api, { loadError, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MonthGrid, addDays } from "@/components/MonthGrid";
import { STATUS, BLOCK_STYLE, fmtDate, euro, nights } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BookingDialog from "@/components/BookingDialog";
import { BookingHistory, HistoryPdfButton } from "@/components/BookingHistory";
import { RegularBadge, GuestNotesBox } from "@/components/GuestBadges";
import { GroupActions } from "@/components/GroupActions";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Columns2, MoveHorizontal, Ban, Copy } from "lucide-react";
import RoomBlockDialog from "@/components/RoomBlockDialog";

export { dayKey } from "@/components/MonthGrid";

const useTwoMonths = () => {
  const [two, setTwo] = useState(() => localStorage.getItem("cal_two_months") === "1");
  useEffect(() => { localStorage.setItem("cal_two_months", two ? "1" : "0"); }, [two]);
  return [two, setTwo];
};

export default function CalendarPage() {
  const [rooms, setRooms] = useState([]);
  const [buildingFilter, setBuildingFilter] = useState(() => localStorage.getItem("cal_building") || "all");
  useEffect(() => { localStorage.setItem("cal_building", buildingFilter); }, [buildingFilter]);
  const visibleRooms = buildingFilter === "all" ? rooms : rooms.filter((r) => (r.building || "nord") === buildingFilter);
  const [bookings, setBookings] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [open, setOpen] = useState(false);
  const [defaults, setDefaults] = useState({});
  const [detail, setDetail] = useState(null);
  const [twoMonths, setTwoMonths] = useTwoMonths();
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const movedRef = useRef(false);

  const [blocks, setBlocks] = useState([]);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockDetail, setBlockDetail] = useState(null);
  const [dupFrom, setDupFrom] = useState(null);
  const [presetRooms, setPresetRooms] = useState(null);

  const load = () => {
    api.get("/rooms").then((r) => setRooms(r.data)).catch(loadError);
    api.get("/bookings").then((r) => setBookings(r.data)).catch(loadError);
    api.get("/room-blocks").then((r) => setBlocks(r.data)).catch(loadError);
  };
  useEffect(() => { load(); }, []);
  const blockItems = blocks.map((bl) => ({ id: bl.id, room_id: bl.room_id, check_in: bl.start_date, check_out: bl.end_date, guest_name: bl.reason, status: "blocked", is_block: true, raw: bl }));
  const allItems = [...bookings, ...blockItems];

  const removeBlock = async (bl, series = false) => {
    try {
      const r = await api.delete(`/room-blocks/${bl.id}${series ? "?series=true" : ""}`);
      toast.success(series ? `Serie rimossa (${r.data.deleted} blocchi)` : "Blocco rimosso");
      setBlockDetail(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const nextCursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  const label = (c) => c.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const shift = (n) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, 1));

  const previewOf = (d) => {
    if (!d) return null;
    const b = d.booking;
    if (d.mode === "move") {
      const delta = Math.round((new Date(d.targetDay + "T00:00:00") - new Date(d.startDay + "T00:00:00")) / 86400000);
      return { ...b, room_id: d.targetRoom, check_in: addDays(b.check_in, delta), check_out: addDays(b.check_out, delta) };
    }
    const co = addDays(d.targetDay, 1);
    return { ...b, check_out: co > b.check_in ? co : addDays(b.check_in, 1) };
  };
  const conflictOf = (p) => !!p && allItems.some((o) => o.id !== p.id && o.room_id === p.room_id && o.status !== "cancelled" && o.check_in < p.check_out && o.check_out > p.check_in);
  const preview = previewOf(drag);
  const conflict = conflictOf(preview);
  const shownBookings = preview ? [preview, ...allItems.filter((b) => b.id !== preview.id)] : allItems;

  const conflictRef = useRef(false);
  conflictRef.current = conflict;

  const onDragStart = (booking, mode, k) => {
    movedRef.current = false;
    const d = { booking, mode, startDay: k, targetDay: k, targetRoom: booking.room_id };
    dragRef.current = d;
    setDrag(d);
  };
  const onCellEnter = (roomId, k) => {
    const d = dragRef.current;
    if (!d) return;
    const nd = { ...d, targetDay: k, targetRoom: d.mode === "move" ? roomId : d.targetRoom };
    if (nd.targetDay !== d.targetDay || nd.targetRoom !== d.targetRoom) movedRef.current = true;
    dragRef.current = nd;
    setDrag(nd);
  };

  useEffect(() => {
    const finish = async () => {
      const d = dragRef.current;
      if (!d) return;
      dragRef.current = null;
      setDrag(null);
      const p = previewOf(d);
      const b = d.booking;
      if (p.check_in === b.check_in && p.check_out === b.check_out && p.room_id === b.room_id) return;
      if (conflictRef.current) { toast.error("Le date si sovrappongono a un'altra prenotazione"); return; }
      try {
        await api.patch(`/bookings/${b.id}/dates`, { room_id: p.room_id, check_in: p.check_in, check_out: p.check_out });
        const prev = { room_id: b.room_id, check_in: b.check_in, check_out: b.check_out };
        toast.success(d.mode === "move" ? "Prenotazione spostata" : "Prenotazione aggiornata", {
          duration: 8000,
          action: {
            label: "Annulla",
            onClick: async () => {
              try {
                await api.patch(`/bookings/${b.id}/dates`, prev);
                toast.success("Spostamento annullato");
                load();
              } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
            },
          },
        });
        load();
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    window.addEventListener("mouseup", finish);
    return () => window.removeEventListener("mouseup", finish);
  }, []);

  const cellClick = (roomId, k, existing) => {
    if (movedRef.current) { movedRef.current = false; return; }
    if (existing?.is_block) { setBlockDetail(existing.raw); return; }
    if (existing) { setDetail(existing); return; }
    setDupFrom(null);
    setPresetRooms(null);
    setDefaults({ defaultRoomId: roomId, defaultDate: k });
    setOpen(true);
  };

  const groupClick = (k) => {
    const next = addDays(k, 1);
    const free = rooms.filter((r) => !allItems.some((o) => o.room_id === r.id && o.status !== "cancelled" && o.check_in < next && o.check_out > k)).map((r) => r.id);
    if (free.length === 0) { toast.error("Nessuna camera libera in questa data"); return; }
    setDupFrom(null);
    setPresetRooms(free);
    setDefaults({ defaultRoomId: free[0], defaultDate: k });
    setOpen(true);
  };

  const hint = conflict ? {
    check_in: preview.check_in, check_out: preview.check_out,
    rooms: new Set(rooms.filter((r) => r.id !== preview.room_id && !conflictOf({ ...preview, room_id: r.id })).map((r) => r.id)),
  } : null;
  const gridProps = { rooms: visibleRooms, bookings: shownBookings, drag, conflict, hint, onCellClick: cellClick, onGroupClick: groupClick, onDragStart, onCellEnter };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Disponibilità</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-1">Calendario Camere</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="cal-prev" onClick={() => shift(-1)}><ChevronLeft className="w-4 h-4" /></Button>
          <div className="font-heading font-semibold capitalize min-w-[160px] text-center" data-testid="cal-month-label">
            {label(cursor)}{twoMonths && <span className="hidden xl:inline"> – {label(nextCursor)}</span>}
          </div>
          <Button variant="outline" size="icon" data-testid="cal-next" onClick={() => shift(1)}><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => { const d = new Date(); setCursor(new Date(d.getFullYear(), d.getMonth(), 1)); }}>Oggi</Button>
          <Button variant="outline" size="sm" data-testid="block-room-button" onClick={() => setBlockOpen(true)} className="text-stone-700">
            <Ban className="w-4 h-4 mr-1.5" />Blocca camera
          </Button>
          <Button variant={twoMonths ? "default" : "outline"} size="sm" data-testid="cal-two-months-toggle" onClick={() => setTwoMonths((v) => !v)} className={`hidden xl:inline-flex ${twoMonths ? "bg-accent text-accent-foreground hover:bg-accent/90" : ""}`}>
            <Columns2 className="w-4 h-4 mr-1.5" />{twoMonths ? "Un mese" : "Due mesi"}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs">
        <div className="inline-flex rounded-full border border-border p-0.5" data-testid="cal-building-filter">
          {[["all", "Tutti gli edifici"], ["nord", "Edificio Nord"], ["sud", "Edificio Sud"]].map(([k, l]) => (
            <button key={k} type="button" data-testid={`cal-building-${k}`} onClick={() => setBuildingFilter(k)} className={`rounded-full px-3 py-1 transition-colors ${buildingFilter === k ? "bg-accent text-accent-foreground" : "hover:bg-secondary"}`}>{l}</button>
          ))}
        </div>
        <span className="text-muted-foreground" data-testid="cal-rooms-count">{visibleRooms.length} camere</span>
        <span className="w-px h-4 bg-border" />
        {Object.entries(STATUS).filter(([k]) => k !== "cancelled").map(([k, s]) => (
          <div key={k} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: s.block }} />{s.label}</div>
        ))}
        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: BLOCK_STYLE.block }} />{BLOCK_STYLE.label}</div>
        <span className="text-muted-foreground flex items-center gap-1 ml-auto"><MoveHorizontal className="w-3.5 h-3.5" /> Trascina una prenotazione per spostarla, il bordo destro per allungarla</span>
      </div>

      <div className={twoMonths ? "grid gap-4 xl:grid-cols-2" : ""} data-testid="calendar-grid">
        <Card className="border-border shadow-sm overflow-hidden">
          {twoMonths && <div className="px-4 py-2 text-xs uppercase tracking-wider font-semibold text-accent border-b border-border capitalize hidden xl:block">{label(cursor)}</div>}
          <MonthGrid cursor={cursor} compact={twoMonths} {...gridProps} />
        </Card>
        {twoMonths && (
          <Card className="border-border shadow-sm overflow-hidden hidden xl:block" data-testid="calendar-grid-second">
            <div className="px-4 py-2 text-xs uppercase tracking-wider font-semibold text-accent border-b border-border capitalize">{label(nextCursor)}</div>
            <MonthGrid cursor={nextCursor} compact {...gridProps} />
          </Card>
        )}
      </div>

      <BookingDialog open={open} onOpenChange={setOpen} rooms={rooms} duplicateFrom={dupFrom} presetRoomIds={presetRooms} {...defaults} onSaved={load} />
      <RoomBlockDialog open={blockOpen} onOpenChange={setBlockOpen} rooms={rooms} onSaved={load} />

      <Dialog open={!!blockDetail} onOpenChange={(v) => !v && setBlockDetail(null)}>
        <DialogContent className="max-w-sm" data-testid="room-block-detail-dialog">
          {blockDetail && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-xl">Camera bloccata</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm">
                <Row label="Camera" value={rooms.find((r) => r.id === blockDetail.room_id)?.name || "—"} />
                <Row label="Motivo" value={blockDetail.reason} />
                <Row label="Dal" value={fmtDate(blockDetail.start_date)} />
                <Row label="Al" value={fmtDate(blockDetail.end_date)} />
                {blockDetail.created_by && <Row label="Creato da" value={blockDetail.created_by} />}
                {blockDetail.series_id && <Row label="Ricorrenza" value="Settimanale" />}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <Button variant="destructive" data-testid="remove-block-button" onClick={() => removeBlock(blockDetail)}>{blockDetail.series_id ? "Rimuovi solo questo" : "Rimuovi blocco"}</Button>
                {blockDetail.series_id && <Button variant="outline" data-testid="remove-series-button" onClick={() => removeBlock(blockDetail, true)} className="text-destructive border-destructive/40">Rimuovi tutta la serie</Button>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-md" data-testid="booking-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2 flex-wrap">{detail.guest_name}<RegularBadge stays={detail.guest_stays} testId="detail-regular-badge" /></DialogTitle></DialogHeader>
              <GuestNotesBox notes={detail.guest_notes} testId="detail-guest-notes" />
              <div className="space-y-2 text-sm">
                <Row label="Camera" value={detail.room_name} />
                <Row label="Stato" value={<Badge className={`${STATUS[detail.status]?.bg} ${STATUS[detail.status]?.text} border-0`}>{STATUS[detail.status]?.label}</Badge>} />
                <Row label="Check-in" value={fmtDate(detail.check_in)} />
                <Row label="Check-out" value={fmtDate(detail.check_out)} />
                <Row label="Notti" value={nights(detail.check_in, detail.check_out)} />
                <Row label="Ospiti" value={detail.num_guests} />
                <Row label="Totale" value={euro(detail.total_amount)} />
                <Row label="Saldo" value={detail.balance > 0 ? euro(detail.balance) : "Saldato"} />
                {detail.guest_phone && <Row label="Telefono" value={detail.guest_phone} />}
              </div>
              {detail.history?.length > 0 && <BookingHistory history={detail.history} rooms={rooms} />}
              <div className="flex flex-wrap gap-2 mt-3">
                <Button variant="outline" size="sm" data-testid="duplicate-booking-button"
                  onClick={() => { setDupFrom(detail); setDefaults({}); setDetail(null); setOpen(true); }}>
                  <Copy className="w-4 h-4 mr-1.5" />Ripeti prenotazione
                </Button>
                {detail.history?.length > 0 && <HistoryPdfButton booking={detail} />}
                <GroupActions booking={detail} onDone={() => { setDetail(null); load(); }} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between border-b border-border/60 py-1.5">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);
